import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  InfoCircle,
  TextInput,
  useToastHook,
} from '@devd-client/devd/components';
import { Field, Formik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import { Select } from 'chakra-react-select';
import {
  customSelectStyles,
  getFormattedDate,
  getRequiredDays,
  MemberOption,
  parseDate,
  scopeOptions,
  TeamOption,
  timeframeOptions,
} from '../../helpers/Goals.utils';
import { validationSchema } from './validationSchema';
import { DatePickerField } from './DatePickerField';
import { TeamSelectComponent } from './TeamSelect';
import { useCreateGoal, useUpdateGoal } from '../../apis';
import { keyframes } from '@emotion/react';
import moment from 'moment';
import { ContributorSelectTableModal } from './ContributorSelectTableModal';
import {
  CreateGoalOverviewValues,
  CreateGoalOverviewFormValues,
  GoalState,
} from '../../helpers/goals.types';

const getInitialValues = (
  goalState: GoalState | undefined
): CreateGoalOverviewFormValues => {
  if (!goalState) {
    return {
      title: '',
      description: '',
      scope: '',
      associatedIds: [],
      scopeTarget: [],
      timeframe: 'QUARTERLY',
      startDate: undefined,
      endDate: undefined,
    };
  }

  return {
    title: goalState?.title || '',
    description: goalState?.description || '',
    scope: goalState?.scope || '',
    associatedIds: goalState?.associatedIds || [],
    timeframe: goalState?.timeframe || 'QUARTERLY',
    startDate: parseDate(goalState.startDate),
    endDate: parseDate(goalState.endDate),
  };
};

interface CreateGoalOverviewTabProps {
  setGoalId: (id: string) => void;
  setGoalScope: (scope: string) => void;
  isEdit?: boolean;
  setIsEdit?: (isEdit: boolean) => void;
  goalState?: GoalState;
  setGoalState?: (goalState: GoalState) => void;
  onNext: () => void;
  goalStateLoading?: boolean;
}

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const CreateGoalOverviewTab = ({
  setGoalId,
  setGoalScope,
  isEdit,
  setIsEdit,
  goalState,
  setGoalState,
  onNext,
  goalStateLoading,
}: CreateGoalOverviewTabProps) => {
  const [newToast] = useToastHook();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { mutate: createGoal, isLoading: createGoalLoading } = useCreateGoal();
  const { mutate: updateGoal, isLoading: updatedGoalLoading } = useUpdateGoal();
  const onSubmit = (values: CreateGoalOverviewFormValues) => {
    if (values?.startDate && values?.endDate) {
      const startDate = moment(values?.startDate);
      const endDate = moment(values?.endDate);
      const daysDiff = endDate.diff(startDate, 'days');
      const requiredDays = getRequiredDays(values?.timeframe);

      if (daysDiff < requiredDays) {
        newToast({
          message: `For ${
            values?.timeframe?.charAt(0)?.toUpperCase() +
            values?.timeframe?.slice(1)?.toLowerCase()
          } timeframe, number of days between start date and end date should be at least ${requiredDays} days`,
          status: 'error',
          duration: 5000,
        });
        return;
      }
    }

    const payload: CreateGoalOverviewValues = {
      title: values.title,
      description: values.description,
      scope: values.scope,
      associatedIds: values.associatedIds,
      timeframe: values.timeframe,
      startDate: values.startDate ? getFormattedDate(values.startDate) : '',
      endDate: values.endDate ? getFormattedDate(values.endDate) : '',
    };

    if (isEdit && goalState?.id) {
      updateGoal(
        { goalId: goalState.id, goalData: payload },
        {
          onSuccess: (data) => {
            newToast({
              message: 'Goal updated successfully!',
              status: 'success',
            });
            queryClient.invalidateQueries(['goalsData']);
            queryClient.invalidateQueries(['goals']);
          },
          onError: (error: any) => {
            newToast({
              message:
                error?.message || 'Failed to update goal. Please try again.',
              status: 'error',
            });
          },
        }
      );
    } else {
      createGoal(payload, {
        onSuccess: (data) => {
          if (data && data?.dto?.id) {
            setGoalId(data?.dto?.id);
            setGoalScope(data?.dto?.scope);
          }

          newToast({
            message: 'Goal created successfully!',
            status: 'success',
          });
          setGoalState?.(data?.dto);
          setIsEdit?.(true);
          onNext();
        },
        onError: (error: any) => {
          newToast({
            message:
              error?.message || 'Failed to create goal. Please try again.',
            status: 'error',
          });
        },
      });
    }
  };

  return (
    <Box position={'relative'} w={'800px'}>
      {goalStateLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          background="linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)"
          animation={`${shimmer} 1.2s infinite ease-in-out`}
          zIndex={1}
          pointerEvents="none"
          maxW={'600px'}
        />
      )}
      <Formik<CreateGoalOverviewFormValues>
        initialValues={getInitialValues(goalState)}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {(props) => {
          const {
            isSubmitting,
            handleSubmit,
            setFieldValue,
            errors,
            touched,
            values,
          } = props;

          const handleSelectScopeTarget = (
            option: TeamOption | MemberOption | (TeamOption | MemberOption)[]
          ) => {
            const currentTargets = Array.isArray(values.associatedIds)
              ? [...values.associatedIds]
              : [];

            if (Array.isArray(option)) {
              const newValues = option
                ?.filter((opt) => !currentTargets?.includes(opt?.value))
                ?.map((opt) => opt?.value);

              const newTargets = [...currentTargets, ...newValues];
              setFieldValue('associatedIds', newTargets);
            } else {
              const exists = currentTargets?.includes(option?.value);

              if (!exists) {
                const newTargets = [...currentTargets, option?.value];
                setFieldValue('associatedIds', newTargets);
              }
            }
          };

          const handleRemoveScopeTarget = (value: string) => {
            const updatedTargets = Array.isArray(values.associatedIds)
              ? values.associatedIds.filter(
                  (target: string) => target !== value
                )
              : [];
            setFieldValue('associatedIds', updatedTargets);
          };

          return (
            <>
              <form onSubmit={handleSubmit}>
                <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                  <GridItem colSpan={2}>
                    <TextInput
                      name="title"
                      label="Goal Name"
                      placeholder="Goal Name"
                      isDisabled={goalStateLoading}
                      tooltipLabel="Goal Name is required. Define a clear, concise name and a detailed description for this Objective."
                      isRequired
                    />
                  </GridItem>

                  <GridItem colSpan={2}>
                    <TextInput
                      name="description"
                      label="Description"
                      inputType="textarea"
                      placeholder="Goal Description"
                      isDisabled={goalStateLoading}
                      isRequired
                    />
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Flex align="center" gap={1} mb={2}>
                      <Text
                        fontSize="sm"
                        fontFamily="heading"
                        fontWeight="semibold"
                        color="text.secondary2"
                      >
                        Scope *
                      </Text>
                      <Tooltip
                        label="Select the level (Organization, Team, Developer) to which this OKR applies. This ensures correct assignment and visibility. These goals do not cascade to other hierarchies."
                        placement="top"
                        hasArrow
                      >
                        <Box>
                          <InfoCircle height={'14px'} width={'14px'} />
                        </Box>
                      </Tooltip>
                    </Flex>
                    <Field name="scope">
                      {({ field, form }: any) => (
                        <FormControl
                          id="scope"
                          isInvalid={form.errors.scope && form.touched.scope}
                        >
                          <Flex align="start" gap={4}>
                            <Box>
                              <Select
                                name="scope"
                                placeholder="Select Scope"
                                options={scopeOptions}
                                value={
                                  scopeOptions.find(
                                    (option) => option.value === values.scope
                                  ) || null
                                }
                                onChange={(option: any) => {
                                  setFieldValue('scope', option?.value || '');
                                  setFieldValue('associatedIds', []);
                                }}
                                chakraStyles={customSelectStyles}
                                components={{
                                  IndicatorSeparator: () => null,
                                }}
                                isDisabled={goalStateLoading}
                              />
                            </Box>

                            <Box flex="1">
                              {values.scope && values.scope !== 'ORG' && (
                                <FormControl
                                  isInvalid={Boolean(
                                    touched.associatedIds &&
                                      errors.associatedIds
                                  )}
                                >
                                  {values.scope === 'CONTRIBUTOR' ? (
                                    <Button
                                      variant="custom-outline"
                                      onClick={onOpen}
                                    >
                                      Select Contributors (
                                      {values.associatedIds?.length || 0})
                                    </Button>
                                  ) : values.scope === 'TEAM' ? (
                                    <TeamSelectComponent
                                      selectedTeams={values.associatedIds || []}
                                      onTeamSelect={(team) =>
                                        handleSelectScopeTarget(team)
                                      }
                                      onTeamRemove={(value) =>
                                        handleRemoveScopeTarget(value)
                                      }
                                    />
                                  ) : null}
                                  <FormErrorMessage position={'absolute'}>
                                    {typeof errors.associatedIds === 'string'
                                      ? errors.associatedIds
                                      : 'This field is required'}
                                  </FormErrorMessage>
                                </FormControl>
                              )}

                              {(!values.scope || values.scope === 'ORG') && (
                                <Input
                                  placeholder={
                                    values.scope === 'ORG'
                                      ? 'Organization'
                                      : 'Select Scope first'
                                  }
                                  isDisabled={true}
                                  value={values.scope === 'ORG' ? 'All' : ''}
                                  bg="gray.100"
                                  cursor="not-allowed"
                                />
                              )}
                            </Box>
                          </Flex>

                          {errors.scope && touched.scope && (
                            <FormErrorMessage>
                              {typeof errors.scope === 'string'
                                ? errors.scope
                                : 'Scope is required'}
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      )}
                    </Field>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Field name="timeframe">
                      {({ field }: any) => (
                        <FormControl id="timeframe">
                          <Flex align="center" gap={1} mb={2}>
                            <Text
                              fontSize="sm"
                              fontWeight="semibold"
                              color="text.secondary2"
                            >
                              Timeframe *
                            </Text>
                            <Tooltip
                              label="Choose the cycle for this OKR (e.g., Weekly, Monthly, Quarterly). This sets the rhythm for tracking and updates."
                              hasArrow
                              placement="top"
                            >
                              <Box>
                                <InfoCircle height={'14px'} width={'14px'} />
                              </Box>
                            </Tooltip>
                          </Flex>
                          <RadioGroup
                            {...field}
                            value={values.timeframe}
                            onChange={(value) => {
                              setFieldValue('timeframe', value);
                            }}
                            id="timeframe"
                          >
                            <Stack direction="row" spacing={'16px'}>
                              {timeframeOptions.map((option) => (
                                <Radio
                                  key={option.value}
                                  color={'primary'}
                                  value={option.value}
                                  size="sm"
                                  spacing="1px"
                                >
                                  <Text color={'text.gray.300'} ml={'-4px'}>
                                    {option.label}
                                  </Text>
                                </Radio>
                              ))}
                            </Stack>
                          </RadioGroup>
                          {errors.timeframe && touched.timeframe && (
                            <FormErrorMessage>
                              {typeof errors.timeframe === 'string'
                                ? errors.timeframe
                                : 'Timeframe is required'}
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      )}
                    </Field>
                  </GridItem>

                  <GridItem colSpan={1}>
                    <DatePickerField
                      name="startDate"
                      label="Start Date"
                      tooltipLabel="Define the exact Start and End dates. Ensure this aligns with your strategic Timeframe. Goal Active Days should have at least a few weeks, months, or quarters to set the Goal."
                      placeholder="Select start date"
                      value={values.startDate}
                      setFieldValue={setFieldValue}
                      touched={touched}
                      errors={errors}
                    />
                  </GridItem>

                  <GridItem colSpan={1}>
                    <DatePickerField
                      name="endDate"
                      label="End Date"
                      secondaryLabel="(Goal will be archived after end date)"
                      placeholder="Select end date"
                      value={values.endDate}
                      setFieldValue={setFieldValue}
                      touched={touched}
                      errors={errors}
                    />
                  </GridItem>
                </Grid>

                <Divider my={'24px'} />
                <Button
                  type="submit"
                  variant="filled"
                  isDisabled={
                    createGoalLoading || updatedGoalLoading || goalStateLoading
                  }
                  isLoading={createGoalLoading || updatedGoalLoading}
                >
                  {isEdit ? 'Update' : 'Submit'}
                </Button>
              </form>
              {isOpen && (
                <ContributorSelectTableModal
                  isOpen={isOpen}
                  onClose={onClose}
                  onSelectContributors={(contributors) => {
                    setFieldValue('associatedIds', contributors);
                  }}
                  initialSelectedContributors={values.associatedIds}
                />
              )}
            </>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CreateGoalOverviewTab;
